import { memo } from "react";

import { LoadingOverlay } from "@components/LoadingOverlay";
import { Pagination } from "@react/components/Pagination";
import { Tabs } from "@react/components/Tabs";
import { ReviewItem } from "@react/sections/ProductReviews/ReviewItem";
import { ReviewItemPlaceholder } from "@react/sections/ProductReviews/ReviewItemPlaceholder";
import { ReviewsSectionSummary } from "@react/sections/ProductReviews/ReviewsSectionSummary";
import {
  type ThemeMode,
  type GroupingData,
  type Review,
  type ReviewsProductData,
  type Score,
} from "@root/tablescape/state";
import { type TTailwindString, tw } from "classnames";

const styles = {
  base: tw(
    "bg-background-primary",
    "text-primary",
    "max-w-screen-xl",
    "mx-auto",
    "relative",
    "py-10",
    "pt-20"
  ),
  body: tw(
    "min-h-[875px]" as TTailwindString,
    "flex",
    "flex-col",
    "justify-end"
  ),
  wrapper: tw("mx-6", "md:mx-12", "mb-5", "flex-1"),
  tabsWrapper: tw("md:mx-12"),
  paginationWrapper: tw("mx-6", "md:mx-12", "mb-5", "md:mt-1"),
};

export type ReviewSummary = {
  count: number | undefined;
  distribution: Record<Score, number> | undefined;
  score: string | undefined;
};

export type Reviews = {
  items: Review[] | undefined;
  pagination:
    | {
        page: number;
        per_page: number;
        total: number;
      }
    | undefined;
};

type Props = {
  currentProduct: ReviewsProductData;
  groupingData: GroupingData | undefined;
  handlePageChange: (page: number) => void;
  handleTabClick: (index: number) => void;
  isAggregate: boolean;
  reviewSummary: ReviewSummary;
  reviewSummaryIsLoading: boolean;
  reviews: Reviews;
  reviewsAreLoading: boolean;
  tabLabels: string[];
  theme: ThemeMode;
};
const ProductReviewsBaseViewBase = ({
  currentProduct,
  groupingData,
  handlePageChange,
  handleTabClick,
  isAggregate,
  reviews,
  reviewsAreLoading,
  reviewSummary,
  reviewSummaryIsLoading,
  tabLabels,
  theme,
}: Props) => {
  return (
    <div className={styles.base}>
      {/* Loading Overlay */}
      {(reviewSummaryIsLoading || reviewsAreLoading) && (
        <LoadingOverlay show={true} />
      )}
      {/* Header */}
      <div className={styles.wrapper}>
        <ReviewsSectionSummary
          distribution={reviewSummary.distribution}
          isAggregate={isAggregate}
          reviewCount={reviewSummary.count}
          reviewScore={reviewSummary.score}
        />
      </div>
      {isAggregate && (
        <div className={styles.tabsWrapper}>
          <Tabs onTabClick={handleTabClick} tabs={tabLabels} theme={theme} />
        </div>
      )}
      {/* Body */}
      {/* initial loading missing the placeholders because reviewItems are undefined */}
      <div className={styles.body}>
        <div className={styles.wrapper}>
          {reviews.items?.map((review, index) => (
            <div key={review.id}>
              {reviewsAreLoading ? (
                <ReviewItemPlaceholder
                  hasImage={
                    index === 0 || index === (reviews.items?.length ?? -1) - 1
                  }
                />
              ) : (
                <ReviewItem
                  content={review.content}
                  createdAt={review.created_at}
                  displayName={review.user.display_name}
                  groupingData={groupingData}
                  id={review.id}
                  images={review.images_data}
                  isVerifiedBuyer={review.verified_buyer}
                  productHandle={currentProduct.handle}
                  rating={review.score}
                  reply={review.comment?.content}
                  title={review.title}
                />
              )}
            </div>
          ))}
        </div>
        <div className={styles.paginationWrapper}>
          {reviews.pagination && (
            <Pagination
              handlePageChange={handlePageChange}
              itemsPerPage={reviews.pagination.per_page}
              selectedPage={reviews.pagination.page}
              theme={theme}
              totalItems={reviews.pagination.total}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const ProductReviewsBaseView = memo(ProductReviewsBaseViewBase);
